import { useState } from 'react'
import { Avatar, Badge, Group, Tabs, Text } from '@mantine/core'
import { profileImageError } from '../lib/profileImageCheck'

// #1079 アカウント(マイページ)共通ヘッダー。SNSのプロフィール画面を踏襲し、
// カバー帯 → 帯にかぶるアバター → 名前/かな/ステータスバッジ → 稼働条件のスタット帯 → セクションタブ
// の順に積む。タブ切り替えは別ページ遷移で、パネルの中身はサーバー側(ERB)が描画する。
//
// avatarInputId が渡された場合(基本情報の編集画面)はアバターがクリック可能になり、
// そのidの<input type="file">を開いて選んだ画像をその場でプレビューする。
export default function AccountHeader({
  userName,
  userKana,
  avatarUrl,
  badges = [],
  stats = [],
  sections = [],
  activeKey,
  avatarInputId,
  primaryAction,
}) {
  const [previewUrl, setPreviewUrl] = useState(null)
  const [avatarError, setAvatarError] = useState(null)
  const initial = userName ? userName.trim().charAt(0) : '?'
  const editable = Boolean(avatarInputId)

  const openFilePicker = () => {
    const input = document.getElementById(avatarInputId)
    if (!input) return
    if (!input.dataset.previewBound) {
      input.addEventListener('change', () => {
        const file = input.files && input.files[0]
        if (!file) return
        // 送信前に拡張子とサイズを見る。NG ならファイルを外し、プレビューも読み込まない
        const error = profileImageError(file)
        setAvatarError(error)
        if (error) {
          input.value = ''
          setPreviewUrl(null)
          return
        }
        const reader = new FileReader()
        reader.onload = (e) => setPreviewUrl(e.target.result)
        reader.readAsDataURL(file)
      })
      input.dataset.previewBound = 'true'
    }
    input.click()
  }

  return (
    <header className="account-header">
      <div className="account-header_cover" aria-hidden="true" />

      <div className="account-header_row">
        <div
          className={`account-header_avatar-wrap${editable ? ' is-editable' : ''}`}
          onClick={editable ? openFilePicker : undefined}
          role={editable ? 'button' : undefined}
          tabIndex={editable ? 0 : undefined}
          onKeyDown={editable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFilePicker() } } : undefined}
          aria-label={editable ? 'プロフィール写真を変更' : undefined}
        >
          {/* 外枠128px - 白リング4px×2 = 120px。sizeはmin-widthも設定するためCSSで上書きせずここで合わせる */}
          <Avatar src={previewUrl || avatarUrl} radius="50%" size={120} alt={userName} className="account-header_avatar">
            {initial}
          </Avatar>
          {editable && (
            <span className="account-header_avatar-overlay">
              <span className="material-icons" aria-hidden="true">photo_camera</span>
              <span>写真を変更</span>
            </span>
          )}
        </div>

        {editable && (
          <button type="button" className="account-header_action" onClick={openFilePicker}>
            <span className="material-icons" aria-hidden="true">photo_camera</span>
            <span>写真を変更</span>
          </button>
        )}

        {editable && avatarError && (
          <Text size="xs" c="red" role="alert">{avatarError}</Text>
        )}

        {primaryAction && (
          <a href={primaryAction.href} className="account-header_action">
            <span className="material-icons" aria-hidden="true">{primaryAction.icon || 'edit'}</span>
            <span>{primaryAction.label}</span>
          </a>
        )}
      </div>

      <div className="account-header_identity">
        <h1 className="account-header_name">{userName || '名前未設定'}</h1>
        {userKana && <p className="account-header_kana">{userKana}</p>}
        {badges.length > 0 && (
          <Group gap={6} className="account-header_badges">
            {badges.map((badge) => (
              <Badge key={badge.label} color={badge.color || 'brand'} variant="light" radius="sm" size="md">
                {badge.label}
              </Badge>
            ))}
          </Group>
        )}
      </div>

      {stats.length > 0 && (
        <dl className="account-header_stats">
          {stats.map((stat) => (
            <div key={stat.label} className="account-header_stat">
              <dt>{stat.label}</dt>
              <dd className={stat.value ? '' : 'is-empty'}>{stat.value || '未設定'}</dd>
            </div>
          ))}
        </dl>
      )}

      {sections.length > 0 && (
        <Tabs
          value={activeKey}
          onChange={(value) => {
            const section = sections.find((s) => s.key === value)
            if (section) window.location.href = section.path
          }}
          className="account-header_tabs"
        >
          <Tabs.List>
            {sections.map((section) => (
              <Tabs.Tab key={section.key} value={section.key}>
                {section.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      )}
    </header>
  )
}
